import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import {MapPin, Clock, Euro, Briefcase, Search, X} from 'lucide-react'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'
import { lumi, isDemoMode } from '../lib/lumi'
import { createCurriculoStore, serializeApplicationCurriculo } from '../lib/curriculoStore'
import { useAuth } from '../hooks/useAuth'
import JobCard from '../components/JobCard'

interface Vaga {
  _id?: string
  id: string
  cargo: string
  restaurante: string
  estabelecimentoNome?: string
  nomeEstabelecimento?: string
  logo: string
  localizacao: string
  tipoContrato: string
  regimeTrabalho?: string
  horasSemanais?: number
  infoContrato?: string
  salario?: string
  valorHora?: number
  setor: string
  descricao: string
  requisitos: string[]
  publicadoEm: string
  createdAt?: string
  dataCriacao?: string
  fotoUsuario?: string
  bannerUsuario?: string
}

// Converte os códigos gravados pelo cadastro de vagas em nomes e filtros.
const cargosConhecidos: Record<string, { nome: string; setor: string }> = {
  barman: { nome: 'Barman', setor: 'barman' },
  garcom: { nome: 'Garçom', setor: 'garcom' },
  churrasqueiro: { nome: 'Churrasqueiro', setor: 'churrasqueiro' },
  cozinheiro: { nome: 'Cozinheiro', setor: 'cozinheiro' },
  passador: { nome: 'Passador de Carne', setor: 'passador' },
  sommelier: { nome: 'Sommelier', setor: 'sommelier' },
  auxiliar_cozinha: { nome: 'Auxiliar de Cozinha', setor: 'ajudante' },
  lava_pratos: { nome: 'Lava-pratos', setor: 'limpeza' },
  recepcionista: { nome: 'Recepcionista', setor: 'recepcao' },
  bailarino: { nome: 'Bailarino', setor: 'bailarino' },
  capo_sala: { nome: 'Chefe de Salão', setor: 'salao' },
}

function classificarCargo(cargo?: string) {
  const nome = cargo?.trim() || 'Vaga'
  const normalizado = nome.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
  const codigo = normalizado.replace(/[\s-]+/g, '_')
  if (cargosConhecidos[codigo]) return cargosConhecidos[codigo]
  // Compatibilidade com títulos livres já existentes, sem presumir garçom.
  const regras: [RegExp, string][] = [
    [/auxiliar.*cozinha|ajudante|commis/, 'ajudante'],
    [/lava.?pratos|lavapiatti|limpeza/, 'limpeza'],
    [/capo.?sala|chefe de salao|maitre/, 'salao'],
    [/recepcion|hostess/, 'recepcao'],
    [/passador/, 'passador'],
    [/churrasqueir/, 'churrasqueiro'],
    [/barman|bartender/, 'barman'],
    [/sommelier/, 'sommelier'],
    [/bailarin|dancar|espetaculo/, 'bailarino'],
    [/cozinheir|cuoco|chef/, 'cozinheiro'],
    [/garcom|garconete|camerier/, 'garcom'],
  ]
  return { nome: nome.replace(/_/g, ' '), setor: regras.find(([regra]) => regra.test(normalizado))?.[1] || 'outros' }
}

const vagasMock: Vaga[] = []; /*
  {
    id: '1',
    cargo: 'Churrasqueiro Especializado',
    restaurante: 'Churrascaria Fogo de Chao',
    logo: '🔥',
    localizacao: 'Milano Centro',
    tipoContrato: 'Tempo Integral',
    salario: '€2.200 - €2.800',
    setor: 'churrasqueiro',
    descricao: 'Buscamos churrasqueiro com experiencia em rodizio brasileiro e cortes nobres.',
    requisitos: ['Experiencia minima de 3 anos', 'Conhecimento de cortes brasileiros', 'Permesso di Soggiorno'],
    publicadoEm: '2 dias atras'
  },
  {
    id: '2',
    cargo: 'Churrasqueiro - Rodizio',
    restaurante: 'Brasa Viva Milano',
    logo: '🥩',
    localizacao: 'Porta Garibaldi',
    tipoContrato: 'Tempo Integral',
    salario: '€2.000 - €2.500',
    setor: 'churrasqueiro',
    descricao: 'Oportunidade para churrasqueiro em novo restaurante brasileiro.',
    requisitos: ['Experiencia com espetos', 'Trabalho em equipe', 'Italiano basico'],
    publicadoEm: '1 semana atras'
  },
  // Barman
  {
    id: '3',
    cargo: 'Barman Cocktail Specialist',
    restaurante: 'Terrazza Lounge',
    logo: '🍸',
    localizacao: 'Navigli',
    tipoContrato: 'Tempo Integral',
    salario: '€1.800 - €2.400',
    setor: 'barman',
    descricao: 'Procuramos barman criativo com conhecimento em coquetelaria classica e moderna.',
    requisitos: ['Experiencia minima 2 anos', 'Conhecimento de mixologia', 'Ingles fluente'],
    publicadoEm: '3 dias atras'
  },
  {
    id: '4',
    cargo: 'Bartender - Restaurante Fine Dining',
    restaurante: 'Ristorante Il Convivio',
    logo: '🍷',
    localizacao: 'Duomo',
    tipoContrato: 'Tempo Integral',
    salario: '€2.000 - €2.600',
    setor: 'barman',
    descricao: 'Bartender para restaurante de alto padrao com foco em drinks autorais.',
    requisitos: ['Formacao em bartending', 'Apresentacao impecavel', 'Italiano intermediario'],
    publicadoEm: '5 dias atras'
  },
  // Garcom
  {
    id: '5',
    cargo: 'Garcom - Servico de Excelencia',
    restaurante: 'Ristorante da Vittorio',
    logo: '🍽️',
    localizacao: 'Brera',
    tipoContrato: 'Tempo Integral',
    salario: '€1.600 - €2.200',
    setor: 'garcom',
    descricao: 'Garcom para atendimento de alto nivel em restaurante estrelado.',
    requisitos: ['Experiencia em fine dining', 'Italiano fluente', 'Postura profissional'],
    publicadoEm: '1 dia atras'
  },
  {
    id: '6',
    cargo: 'Cameriere - Churrascaria',
    restaurante: 'Rodizio Brasil',
    logo: '🥘',
    localizacao: 'Isola',
    tipoContrato: 'Tempo Integral',
    salario: '€1.500 - €1.900',
    setor: 'garcom',
    descricao: 'Garcom para churrascaria brasileira com sistema de rodizio.',
    requisitos: ['Experiencia com rodizio', 'Dinamismo', 'Disponibilidade finais de semana'],
    publicadoEm: '4 dias atras'
  },
  // Cozinheiro
  {
    id: '7',
    cargo: 'Cuoco - Cucina Italiana',
    restaurante: 'Trattoria Milano',
    logo: '👨‍🍳',
    localizacao: 'Centrale',
    tipoContrato: 'Tempo Integral',
    salario: '€1.800 - €2.400',
    setor: 'cozinheiro',
    descricao: 'Cozinheiro para trattoria tradicional italiana.',
    requisitos: ['Experiencia em cozinha italiana', 'Conhecimento de massas', 'Trabalho sob pressao'],
    publicadoEm: '6 dias atras'
  },
  {
    id: '8',
    cargo: 'Chef de Partie - Grill',
    restaurante: 'Steakhouse Premium',
    logo: '🔪',
    localizacao: 'Porta Venezia',
    tipoContrato: 'Tempo Integral',
    salario: '€2.200 - €2.800',
    setor: 'cozinheiro',
    descricao: 'Chef especializado em grelhados e carnes nobres.',
    requisitos: ['Experiencia minima 4 anos', 'Especializacao em carnes', 'Lideranca'],
    publicadoEm: '2 dias atras'
  },
  // Limpeza
  {
    id: '9',
    cargo: 'Auxiliar de Limpeza - Restaurante',
    restaurante: 'Gruppo Ristoranti Milano',
    logo: '🧹',
    localizacao: 'Varias localizacoes',
    tipoContrato: 'Tempo Integral',
    salario: '€1.200 - €1.500',
    setor: 'limpeza',
    descricao: 'Auxiliar de limpeza para rede de restaurantes.',
    requisitos: ['Experiencia em limpeza profissional', 'Disponibilidade de horario', 'Documentacao em dia'],
    publicadoEm: '1 semana atras'
  },
  // Sommelier
  {
    id: '10',
    cargo: 'Sommelier - Ristorante Stellato',
    restaurante: 'Il Luogo di Aimo e Nadia',
    logo: '🍇',
    localizacao: 'Porta Romana',
    tipoContrato: 'Tempo Integral',
    salario: '€2.500 - €3.500',
    setor: 'sommelier',
    descricao: 'Sommelier para restaurante estrelado Michelin.',
    requisitos: ['Certificacao AIS ou WSET', 'Conhecimento profundo de vinhos italianos', 'Ingles e Italiano fluentes'],
    publicadoEm: '3 dias atras'
  },
  {
    id: '11',
    cargo: 'Assistente Sommelier',
    restaurante: 'Enoteca Moderna',
    logo: '🍾',
    localizacao: 'Navigli',
    tipoContrato: 'Tempo Integral',
    salario: '€1.800 - €2.200',
    setor: 'sommelier',
    descricao: 'Assistente de sommelier para enoteca com restaurante anexo.',
    requisitos: ['Curso de sommelier', 'Paixao por vinhos', 'Atendimento ao cliente'],
    publicadoEm: '5 dias atras'
  },
  // Bailarinos
  {
    id: '12',
    cargo: 'Bailarino - Show Brasileiro',
    restaurante: 'Churrascaria Tropical',
    logo: '💃',
    localizacao: 'Milano Centro',
    tipoContrato: 'Freelance',
    salario: '€150 - €250/noite',
    setor: 'bailarino',
    descricao: 'Bailarinos para show brasileiro em churrascaria tematica.',
    requisitos: ['Experiencia em danca brasileira', 'Samba, forro, axe', 'Figurino proprio'],
    publicadoEm: '2 dias atras'
  },
  // Passador de Carne
  {
    id: '13',
    cargo: 'Passador de Carne - Rodizio',
    restaurante: 'Churrascaria Brasil Milano',
    logo: '🍖',
    localizacao: 'Porta Garibaldi',
    tipoContrato: 'Tempo Integral',
    salario: '€1.600 - €2.000',
    setor: 'passador',
    descricao: 'Passador de carne para rodizio brasileiro de alto padrao.',
    requisitos: ['Experiencia com rodizio', 'Boa comunicacao', 'Apresentacao impecavel'],
    publicadoEm: '4 dias atras'
  },
  {
    id: '14',
    cargo: 'Passador Especializado',
    restaurante: 'Espeto de Ouro',
    logo: '🥓',
    localizacao: 'Isola',
    tipoContrato: 'Tempo Integral',
    salario: '€1.700 - €2.100',
    setor: 'passador',
    descricao: 'Passador com conhecimento de cortes nobres e tecnicas de apresentacao.',
    requisitos: ['Experiencia minima 2 anos', 'Conhecimento de cortes', 'Italiano basico'],
    publicadoEm: '1 semana atras'
  },
  // Ajudante de Cozinheiro
  {
    id: '15',
    cargo: 'Ajudante de Cozinha',
    restaurante: 'Ristorante Il Castello',
    logo: '🥄',
    localizacao: 'Brera',
    tipoContrato: 'Tempo Integral',
    salario: '€1.300 - €1.600',
    setor: 'ajudante',
    descricao: 'Ajudante de cozinha para restaurante tradicional.',
    requisitos: ['Experiencia em cozinha', 'Higiene e organizacao', 'Trabalho em equipe'],
    publicadoEm: '3 dias atras'
  },
  {
    id: '16',
    cargo: 'Commis de Cozinha',
    restaurante: 'Trattoria del Corso',
    logo: '🍳',
    localizacao: 'Duomo',
    tipoContrato: 'Tempo Integral',
    salario: '€1.400 - €1.700',
    setor: 'ajudante',
    descricao: 'Commis para auxiliar equipe de cozinha em trattoria movimentada.',
    requisitos: ['Vontade de aprender', 'Dinamismo', 'Disponibilidade imediata'],
    publicadoEm: '6 dias atras'
  },
]*/

const setores = [
  { id: 'todos', nome: 'Todas as Vagas', count: vagasMock.length },
  { id: 'churrasqueiro', nome: 'Churrasqueiro', count: vagasMock.filter(v => v.setor === 'churrasqueiro').length },
  { id: 'barman', nome: 'Barman', count: vagasMock.filter(v => v.setor === 'barman').length },
  { id: 'garcom', nome: 'Garçom', count: vagasMock.filter(v => v.setor === 'garcom').length },
  { id: 'cozinheiro', nome: 'Cozinheiro', count: vagasMock.filter(v => v.setor === 'cozinheiro').length },
  { id: 'limpeza', nome: 'Limpeza', count: vagasMock.filter(v => v.setor === 'limpeza').length },
  { id: 'sommelier', nome: 'Sommelier', count: vagasMock.filter(v => v.setor === 'sommelier').length },
  { id: 'bailarino', nome: 'Espetáculo Brasileiro', count: vagasMock.filter(v => v.setor === 'bailarino').length },
  { id: 'passador', nome: 'Passador de Carne', count: vagasMock.filter(v => v.setor === 'passador').length },
  { id: 'ajudante', nome: 'Ajudante de Cozinha', count: vagasMock.filter(v => v.setor === 'ajudante').length },
  { id: 'recepcao', nome: 'Recepção', count: 0 },
  { id: 'salao', nome: 'Chefe de Salão', count: 0 },
  { id: 'outros', nome: 'Outros cargos', count: 0 },
]

function Vagas() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [setorAtivo, setSetorAtivo] = useState('todos')
  const [busca, setBusca] = useState('')
  const [vagasAtivas, setVagasAtivas] = useState<Vaga[]>(vagasMock)
  const [setoresComContagem, setSetoresComContagem] = useState(setores)
  const [vagaSelecionada, setVagaSelecionada] = useState<Vaga | null>(null)
  const { user } = useAuth()
  const [usuarioLogado, setUsuarioLogado] = useState<any>(null)
  const [candidatando, setCandidatando] = useState(false)
  const [candidaturaSucesso, setCandidaturaSucesso] = useState(false)

  const [compartilharCurriculo, setCompartilharCurriculo] = useState(false);
  const podeCompartilharCv = isDemoMode || import.meta.env.VITE_PRIVATE_APPLICATION_CV_READY === 'true';
  useEffect(() => { setCompartilharCurriculo(false); }, [vagaSelecionada?.id]);

  // Fetch logged user data to get name, photo and banner for the application
  useEffect(() => {
    const fetchUser = async () => {
      if (user) {
        try {
          const userId = user.userId;
          const { list } = await lumi.entities.user_profiles.list({
            filter: { userId }
          })
          if (list && list.length > 0) {
            setUsuarioLogado(list[0])
          } else {
            // Fallback: usa dados do auth
            setUsuarioLogado({
              userId,
              userName: (user as any).userName || user.email,
              profileImageUrl: '',
              banner: ''
            })
          }
        } catch (e) {
          console.error("Erro ao buscar perfil do usuário", e)
        }
      }
    }
    fetchUser()
  }, [user])

  useEffect(() => {
    const fetchVagasReais = async () => {
      try {
        const { list } = await lumi.entities.vagas.list({
          sort: { createdAt: -1 }
        })
        
        if (list && list.length > 0) {
          // Identifica vagas sem foto/banner para buscar do perfil do estabelecimento criador
          const idsSemImagem = Array.from(new Set(
            list
              .filter((v: any) => (!v.fotoUsuario || !v.bannerUsuario) && v.userId)
              .map((v: any) => v.userId as string)
          ))

          // Busca os perfis dos estabelecimentos que precisam de imagem
          const perfisPorUserId: Record<string, { profileImageUrl?: string; bannerUrl?: string }> = {}
          if (idsSemImagem.length > 0) {
            try {
              const { list: perfis } = await lumi.entities.user_profiles.list({
                filter: { userId: { $in: idsSemImagem } }
              })
              if (perfis && perfis.length > 0) {
                perfis.forEach((p: any) => {
                  if (p.userId) {
                    perfisPorUserId[p.userId] = {
                      profileImageUrl: p.profileImageUrl,
                      bannerUrl: p.bannerUrl
                    }
                  }
                })
              }
            } catch (errPerfis) {
              console.warn('Não foi possível enriquecer vagas com perfis dos estabelecimentos:', errPerfis)
            }
          }

          const vagasReais: Vaga[] = list.map((v: any) => {
            const perfilDono = v.userId ? perfisPorUserId[v.userId] : undefined
            const fotoUsuario = v.fotoUsuario || perfilDono?.profileImageUrl || ''
            const bannerUsuario = v.bannerUsuario || perfilDono?.bannerUrl || ''
            return {
              ...v,
              id: v._id || Date.now().toString(),
              cargo: classificarCargo(v.cargo).nome,
              restaurante: v.titulo || 'Restaurante',
              logo: '🍽️',
              localizacao: v.localizacao || 'Milão, Itália',
              tipoContrato: v.tipoContrato || 'N/A',
              salario: v.valorHora ? `€${v.valorHora}/h` : undefined,
              valorHora: v.valorHora,
              setor: classificarCargo(v.cargo).setor,
              descricao: v.descricao || '',
              requisitos: v.detalhesContrato ? [v.detalhesContrato] : ['Sem requisitos especificados'],
              publicadoEm: v.createdAt ? new Date(v.createdAt).toLocaleDateString('pt-BR') : 'Recente',
              fotoUsuario,
              bannerUsuario
            }
          })
          
          const todasVagas = [...vagasReais, ...vagasMock]
          setVagasAtivas(todasVagas)
          
          // Atualiza as contagens
          const novasSetores = setores.map(setor => ({
            ...setor,
            count: setor.id === 'todos' ? todasVagas.length : todasVagas.filter(v => v.setor === setor.id).length,
          }))
          setSetoresComContagem(novasSetores)
        }
      } catch (e) {
        console.error('Erro ao buscar vagas reais:', e)
      }
    }
    
    fetchVagasReais()
  }, [])

  useEffect(() => {
    const setorParam = searchParams.get('setor')
    if (setorParam && setoresComContagem.find(s => s.id === setorParam)) {
      setSetorAtivo(setorParam)
    }
  }, [searchParams, setoresComContagem])

  const vagasFiltradas = vagasAtivas.filter(vaga => {
    const matchSetor = setorAtivo === 'todos' || vaga.setor === setorAtivo
    const matchBusca = busca === '' || 
      vaga.cargo.toLowerCase().includes(busca.toLowerCase()) ||
      vaga.restaurante.toLowerCase().includes(busca.toLowerCase()) ||
      vaga.localizacao.toLowerCase().includes(busca.toLowerCase())
    return matchSetor && matchBusca
  })

  const handleSetorChange = (setorId: string) => {
    setSetorAtivo(setorId)
    if (setorId === 'todos') {
      setSearchParams({})
    } else {
      setSearchParams({ setor: setorId })
    }
  }

  const handleCandidatar = async () => {
    if (candidatando) return
    if (!user) {
      alert("Por favor, faça login para se candidatar.")
      return
    }
    if (!usuarioLogado) {
       alert("Perfil não carregado corretamente.")
       return
    }
    if (!vagaSelecionada) return
    
    if (usuarioLogado.profileType !== 'trabalhador') {
      alert('Apenas perfis de trabalhador podem se candidatar. Complete seu cadastro se necessário.'); return;
    }
    if ((vagaSelecionada as any).userId === user.userId) { alert('Você não pode se candidatar à própria vaga.'); return; }

    
    setCandidatando(true)
    try {
      const { list: currentJobs } = await lumi.entities.vagas.list({ filter: { _id: vagaSelecionada.id }, limit: 1 });
      if (!currentJobs?.length || currentJobs[0].ativa === false) { alert('Esta vaga não está mais disponível.'); return; }
      // Avoid duplicate applications
      const candidatoIdCheck = user.userId;
      const { list: existing } = await lumi.entities.candidaturas.list({
        filter: { 
          vagaId: vagaSelecionada.id,
          candidatoId: candidatoIdCheck 
        }
      })
      
      if (existing && existing.length > 0) {
        alert("Você já se candidatou para essa vaga.")
        setCandidatando(false)
        return
      }

      // Tenta buscar dados completos do estabelecimento para enriquecer a candidatura
      let estabelecimentoNome = (vagaSelecionada as any).userName || vagaSelecionada.restaurante || 'Estabelecimento';
      const estabelecimentoId = currentJobs[0].userId;
      if (!estabelecimentoId) { alert('Esta vaga precisa ser revisada pelo estabelecimento.'); return; }
      try {
        if (estabelecimentoId && estabelecimentoId !== 'unknown') {
          const { list: estProfiles } = await lumi.entities.user_profiles.list({
            filter: { userId: estabelecimentoId },
            limit: 1
          });
          if (estProfiles && estProfiles.length > 0) {
            estabelecimentoNome = estProfiles[0].userName || estProfiles[0].nome || estabelecimentoNome;
          }
        }
      } catch (e) {
        console.warn("Não foi possível buscar nome do estabelecimento", e);
      }

      let curriculoSnapshot: string | undefined;
      if (podeCompartilharCv && compartilharCurriculo) {
        const store = createCurriculoStore({ cloud: isDemoMode || import.meta.env.VITE_PRIVATE_CV_READY === 'true', storage: window.localStorage, entity: lumi.entities.curriculos });
        const cv = await store.load(user.userId);
        if (!cv) { alert('Salve seu currículo no perfil antes de compartilhá-lo, ou desmarque a opção para enviar somente a candidatura.'); return; }
        // Send professional fields only; no nationality, immigration or other unrelated details.
        curriculoSnapshot = serializeApplicationCurriculo(cv);
      }
      const nowISO = new Date().toISOString();
      const candidatoUserId = user.userId;
      await lumi.entities.candidaturas.create({
        ...(curriculoSnapshot ? { curriculoSnapshot, curriculoCompartilhadoEm: nowISO } : {}),
        vagaId: vagaSelecionada.id,
        vagaCargo: vagaSelecionada.cargo,
        vagaTitulo: currentJobs[0].titulo || vagaSelecionada.cargo,
        vagaDescricao: vagaSelecionada.descricao || '',
        vagaLocalizacao: vagaSelecionada.localizacao || '',
        vagaSalario: vagaSelecionada.salario || '',
        vagaBanner: vagaSelecionada.bannerUsuario || '',
        vagaFoto: vagaSelecionada.fotoUsuario || '',
        estabelecimentoId,
        estabelecimentoNome,
        candidatoId: candidatoUserId,
        candidatoNome: (usuarioLogado as any).userName || usuarioLogado.nome || (user as any).userName || user.email,
        candidatoFoto: usuarioLogado.profileImageUrl || usuarioLogado.photoUrl || '',
        candidatoBanner: usuarioLogado.bannerUrl || '',
        candidatoEmail: user.email || '',
        dataCandidatura: nowISO,
        creator: candidatoUserId,
        createdAt: nowISO,
        updatedAt: nowISO,
        status: 'nova'
      })
      
      setCandidaturaSucesso(true)
      setTimeout(() => {
        setCandidaturaSucesso(false)
        setVagaSelecionada(null)
      }, 2000)
      
    } catch (e) {
      console.error("Erro ao se candidatar", e)
      alert("Houve um erro ao processar sua candidatura. Tente novamente.")
    } finally {
      setCandidatando(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1a1a1a] to-black">
      <Navbar />
      
      {/* Header */}
      <div className="bg-gradient-to-r from-[#5D4037] to-[#3E2723] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">
            Vagas Disponiveis em <span className="text-[#E53935]">Milano</span>
          </h1>
          <p className="text-xl text-gray-200 mb-8">
            Encontre sua proxima oportunidade na area de gastronomia e hospitalidade
          </p>
          
          {/* Busca */}
          <div className="relative max-w-2xl">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Buscar por cargo, restaurante ou localizacao..."
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              className="w-full pl-12 pr-4 py-4 bg-white/10 backdrop-blur-md border-2 border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-[#E53935] transition-all"
            />
          </div>
        </div>
      </div>

      {/* Filtros por Setor */}
      <div className="bg-[#2a2a2a] border-b border-[#5D4037]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-2 overflow-x-auto py-4 scrollbar-hide">
            {setoresComContagem.map((setor) => (
              <button
                key={setor.id}
                onClick={() => handleSetorChange(setor.id)}
                className={`px-6 py-3 rounded-lg font-semibold whitespace-nowrap transition-all ${
                  setorAtivo === setor.id
                    ? 'bg-gradient-to-r from-[#C62828] to-[#E53935] text-white shadow-lg scale-105'
                    : 'bg-[#3E2723] text-gray-300 hover:bg-[#5D4037]'
                }`}
              >
                {setor.nome} ({setor.count})
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Lista de Vagas */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-6 text-gray-300">
          <span className="text-xl font-semibold text-white">{vagasFiltradas.length}</span> vagas encontradas
        </div>

        {vagasFiltradas.length === 0 ? (
          <div className="text-center py-16">
            <Briefcase className="mx-auto mb-4 text-gray-500" size={64} />
            <h3 className="text-2xl font-bold text-white mb-2">Nenhuma vaga encontrada</h3>
            <p className="text-gray-400">Tente ajustar os filtros ou a busca</p>
          </div>
        ) : (
          <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,280px),300px))] justify-center gap-5 pb-12">
            {vagasFiltradas.map((vaga) => (
              <JobCard key={vaga.id}
                cargo={vaga.cargo}
                estabelecimento={vaga.estabelecimentoNome || vaga.nomeEstabelecimento}
                descricao={vaga.descricao} localizacao={vaga.localizacao}
                valorHora={vaga.valorHora} foto={vaga.fotoUsuario} capa={vaga.bannerUsuario}
                data={vaga.createdAt || vaga.dataCriacao} contrato={vaga.tipoContrato}
                onDetails={() => setVagaSelecionada(vaga)} />
            ))}
          </div>
        )}
      </div>

      {/* Modal de Detalhes da Vaga */}
      {vagaSelecionada && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 bg-black/80 backdrop-blur-sm">
          <div className="bg-[#1e1e1e] border border-[#5D4037] rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-y-auto relative flex flex-col shadow-2xl">
            <button 
              onClick={() => setVagaSelecionada(null)} 
              className="absolute top-4 right-4 z-20 bg-black/60 backdrop-blur-md rounded-full p-2 text-white hover:bg-[#E53935] transition-colors shadow-lg"
              aria-label="Fechar"
            >
              <X size={24} />
            </button>
            
            <div className="h-[180px] sm:h-[220px] w-full bg-[#111] relative shrink-0">
               <img src={vagaSelecionada.bannerUsuario || 'https://images.unsplash.com/photo-1514933651103-005eec06c04b?q=80&w=1000&auto=format&fit=crop'} alt="Banner" className="w-full h-full object-cover opacity-80" />
               <div className="absolute inset-0 bg-gradient-to-t from-[#1e1e1e] to-transparent h-1/2 bottom-0 top-auto"></div>
            </div>
            
            <div className="px-6 sm:px-10 pb-8 relative flex flex-col">
               <div className="w-[100px] h-[100px] sm:w-[120px] sm:h-[120px] rounded-full overflow-hidden border-[4px] border-[#1e1e1e] shadow-xl shrink-0 bg-[#2a2a2a] -mt-[50px] sm:-mt-[60px] mb-4 z-10 mx-auto sm:mx-0">
                  {vagaSelecionada.fotoUsuario ? (
                    <img src={vagaSelecionada.fotoUsuario} alt="Logo" className="w-full h-full object-cover bg-white" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#C62828] to-[#E53935]">
                       <Briefcase className="text-white" size={40} />
                    </div>
                  )}
               </div>
               
               <div className="text-center sm:text-left">
                 <h2 className="text-2xl sm:text-3xl font-bold text-white mb-1 capitalize">{vagaSelecionada.cargo}</h2>
                 <p className="text-lg text-gray-300 font-semibold mb-6">{vagaSelecionada.restaurante}</p>
               </div>
               
               <div className="flex flex-wrap justify-center sm:justify-start gap-3 mb-8">
                  <div className="flex items-center gap-2 text-gray-300 bg-[#2a2a2a] px-3.5 py-2 rounded-lg border border-[#3a3a3a]">
                    <MapPin size={16} className="text-[#E53935]" />
                    <span className="text-sm">{vagaSelecionada.localizacao}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-300 bg-[#2a2a2a] px-3.5 py-2 rounded-lg border border-[#3a3a3a]">
                    <Clock size={16} className="text-[#E53935]" />
                    <span className="text-sm capitalize">{({ fixa: 'Fixo', temporaria: 'Temporário', diaria: 'Diária / Freelance' } as Record<string, string>)[vagaSelecionada.tipoContrato] || vagaSelecionada.tipoContrato}</span>
                  </div>
                  {vagaSelecionada.salario && (
                    <div className="flex items-center gap-2 text-gray-300 bg-[#2a2a2a] px-3.5 py-2 rounded-lg border border-[#3a3a3a]">
                      <Euro size={16} className="text-[#E53935]" />
                      <span className="text-sm font-semibold text-[#E53935]">{vagaSelecionada.salario}</span>
                    </div>
                  )}
               </div>
               
               <section className="mb-8 p-4 rounded-xl border border-[#E53935]/25 bg-[#2a2a2a]">
                 <h3 className="text-xl font-bold text-white mb-3">Jornada e contrato</h3>
                 <dl className="grid sm:grid-cols-2 gap-3 text-gray-300">
                   <div><dt className="text-sm text-gray-400">Regime</dt><dd>{({ full_time: 'Tempo integral', part_time: 'Tempo parcial' } as Record<string, string>)[vagaSelecionada.regimeTrabalho || ''] || vagaSelecionada.regimeTrabalho || 'Não informado'}</dd></div>
                   <div><dt className="text-sm text-gray-400">Horas semanais</dt><dd>{vagaSelecionada.horasSemanais ? `${vagaSelecionada.horasSemanais} horas` : 'Não informadas'}</dd></div>
                 </dl>
                 <p className="text-gray-300 whitespace-pre-wrap break-words mt-4">{vagaSelecionada.infoContrato || 'Detalhes contratuais não informados pelo estabelecimento.'}</p>
               </section>
               <div className="space-y-8">
                 <div>
                   <h3 className="text-xl font-bold text-white mb-4 pb-2 border-b border-[#3a3a3a]">Descrição da Vaga</h3>
                   <p className="text-gray-300 leading-relaxed whitespace-pre-wrap">{vagaSelecionada.descricao}</p>
                 </div>
                 
                 <div>
                   <h3 className="text-xl font-bold text-white mb-4 pb-2 border-b border-[#3a3a3a]">Requisitos</h3>
                   <ul className="space-y-3">
                      {vagaSelecionada.requisitos.map((req, idx) => (
                        <li key={idx} className="text-gray-300 flex items-start gap-3">
                          <span className="text-[#E53935] mt-1">•</span>
                          <span>{req}</span>
                        </li>
                      ))}
                   </ul>
                 </div>
               </div>

               {user && usuarioLogado?.profileType === 'trabalhador' && (
                 podeCompartilharCv ? <label className="mt-6 flex items-start gap-3 text-sm text-gray-300">
                   <input type="checkbox" checked={compartilharCurriculo} onChange={e => setCompartilharCurriculo(e.target.checked)} disabled={candidatando || candidaturaSucesso} className="mt-1" />
                   Compartilhar uma cópia do currículo salvo e seus contatos com este estabelecimento nesta candidatura.
                 </label> : <p className="mt-6 text-sm text-gray-400">O compartilhamento automático de currículo ainda não está disponível. Combine o envio do PDF pelo chat.</p>
               )}
               <div className="mt-10 pt-6 border-t border-[#3a3a3a] flex flex-col sm:flex-row items-center justify-between gap-4">
                 <span className="text-sm text-gray-500">Publicado em: {vagaSelecionada.publicadoEm}</span>
                 <button 
                  onClick={handleCandidatar}
                  disabled={candidatando || candidaturaSucesso}
                  className={`px-10 py-3.5 rounded-xl font-bold w-full sm:w-auto text-lg transition-all ${
                    candidaturaSucesso 
                      ? 'bg-green-600 text-white' 
                      : candidatando
                      ? 'bg-gray-600 text-gray-300 cursor-not-allowed'
                      : 'bg-gradient-to-r from-[#C62828] to-[#E53935] text-white hover:shadow-lg hover:shadow-[#E53935]/30 hover:-translate-y-1'
                  }`}
                 >
                   {candidaturaSucesso ? 'Candidatura Enviada!' : candidatando ? 'Candidatando...' : 'Candidatar-se à vaga'}
                 </button>
               </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  )
}

export default Vagas
